import { useIsAuthenticated, useMsal } from "@azure/msal-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronDown, ChevronRight, Clock, LogOut } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Link, Navigate, NavLink, Route, Routes, useLocation } from "react-router-dom";

import {
  capturarHsc, getB2bPendientes, getEstadoCredenciales, getHscUltimo, getReaUltimo,
} from "@/lib/api";
import { DEV_BYPASS_AUTH } from "@/lib/auth";
import { AuthzProvider, ROL_LABEL, usePermisos } from "@/lib/authz";
import { BADGE_VIGENCIA, estadoVigencia, fmtFecha } from "@/lib/fiel";
import { BusquedaGlobal, EstrellaFav, FavoritosProvider, useFavoritos } from "@/components/Barra";
import { AdminPage } from "@/pages/AdminPage";
import { AuditoriaPage } from "@/pages/AuditoriaPage";
import { CorreoEscenariosPage } from "@/pages/CorreoEscenariosPage";
import { ContrapartesPage } from "@/pages/ContrapartesPage";
import { ContratosPage } from "@/pages/ContratosPage";
import { CredencialesPage } from "@/pages/CredencialesPage";
import { EscenariosPage } from "@/pages/EscenariosPage";
import { FacturacionPage } from "@/pages/FacturacionPage";
import { FacturasCompraPage } from "@/pages/FacturasCompraPage";
import { FacturasRecibidasPage } from "@/pages/FacturasRecibidasPage";
import { FacturasServiciosPage } from "@/pages/FacturasServiciosPage";
import { FacturaDetallePage, FacturasPage } from "@/pages/FacturasPage";
import { LoginPage } from "@/pages/LoginPage";
import { SombraCenacePage } from "@/pages/SombraCenacePage";
import { SombraPage } from "@/pages/SombraPage";
import { SombraGuardadaDetallePage, SombrasGuardadasPage } from "@/pages/SombrasGuardadasPage";
import { B2bPage } from "@/pages/B2bPage";
import { B2bUtilidadPage } from "@/pages/B2bUtilidadPage";
import { IntercambiosPage } from "@/pages/IntercambiosPage";
import { PronosticosLuxemPage } from "@/pages/PronosticosLuxemPage";
import { ArbitrajePage } from "@/pages/ArbitrajePage";
import { ArbitrajeCentrosCargaPage } from "@/pages/ArbitrajeCentrosCargaPage";
import { NominacionDartPage } from "@/pages/NominacionDartPage";
import { NominacionPage } from "@/pages/NominacionPage";
import { MovimientosBancariosPage } from "@/pages/MovimientosBancariosPage";
import { TipoCambioConfigPage } from "@/pages/TipoCambioConfigPage";
import { ProyeccionCajaPage } from "@/pages/ProyeccionCajaPage";
import { ProyeccionesPage } from "@/pages/ProyeccionesPage";
import { RegistroAutomatizadoPage } from "@/pages/RegistroAutomatizadoPage";
import { MatrizPage } from "@/pages/MatrizPage";
import { NodosCauchyPage } from "@/pages/NodosCauchyPage";
import { ParametrosCauchyPage } from "@/pages/ParametrosCauchyPage";
import { PnlContablePage } from "@/pages/PnlContablePage";
import { MemoriaReaPage } from "@/pages/MemoriaReaPage";
import { ReporteCajaPage } from "@/pages/ReporteCajaPage";
import { GarantiasPage } from "@/pages/GarantiasPage";
import { VarDartPage } from "@/pages/VarDartPage";
import { OficiosPage } from "@/pages/OficiosPage";
import { PosicionesMercadoPage } from "@/pages/PosicionesMercadoPage";
import { PortafolioPage } from "@/pages/PortafolioPage";
import { ReporteInventarioPage } from "@/pages/ReporteInventarioPage";
import { EdoctasList } from "@/pages/EdoctasList";
import { EdoctaDetail } from "@/pages/EdoctaDetail";
import { StrikeActivacionPage } from "@/pages/StrikeActivacionPage";
import { TarifasReguladasPage } from "@/pages/TarifasReguladasPage";
import { TbfinPage } from "@/pages/TbfinPage";

// Navegación: cada entrada se muestra si el usuario tiene alguno de sus permisos.
type NavItem = { to: string; label: string; perm: string | string[]; end?: boolean };
// Submenú dentro de un menú (se abre a la derecha al pasar el mouse).
type NavGrupo = { grupo: string; items: NavItem[] };
type NavEntrada = NavItem | NavGrupo;

const esGrupo = (n: NavEntrada): n is NavGrupo => "grupo" in n;
const hojas = (ns: NavEntrada[]): NavItem[] => ns.flatMap((n) => (esGrupo(n) ? n.items : [n]));

// Menú "Operación": lo que se toca en el día a día.
const NAV_OPERACION: NavEntrada[] = [
  { to: "/intercambios", label: "Importaciones y Exportaciones", perm: "intercambios" },
  { to: "/registro-automatizado", label: "Registro Automatizado (SAT)",
    perm: "registro_automatizado" },
  { to: "/calendario", label: "Calendario", perm: "calendario" },
  { to: "/oficios", label: "Oficios (folio)", perm: "oficios" },
  { to: "/posiciones-mercado", label: "Posiciones de mercado", perm: "posiciones_mercado" },
  { to: "/portafolio", label: "Portafolio CEL / i-REC", perm: "portafolio_cel_irec" },
  // Lo que se opera con la contraparte del suministro (Luxem): pronóstico,
  // arbitraje y la nominación que resulta de ambos.
  { grupo: "Contrapartes", items: [
    { to: "/pronosticos-luxem", label: "Pronósticos Luxem", perm: "pronosticos_luxem" },
    { to: "/arbitraje", label: "Arbitraje", perm: "arbitraje", end: true },
    { to: "/nominacion", label: "Nominación a contraparte", perm: "nominacion", end: true },
    { to: "/nominacion/dart", label: "DART del arbitraje", perm: "nominacion" },
  ] },
  { grupo: "Contabilidad", items: [
    { to: "/movimientos-bancarios", label: "Movimientos bancarios", perm: "movimientos_bancarios" },
    { to: "/proyeccion-caja", label: "Proyección de Caja", perm: "movimientos_bancarios" },
    { to: "/garantias", label: "Garantías y préstamos", perm: "movimientos_bancarios" },
  ] },
  { to: "/tbfin", label: "Cálculo TBFin", perm: ["tbfin", "editar_folio"] },
  { to: "/var-dart", label: "VaR DART (Montecarlo)", perm: "parametros_cauchy" },
];

// Menú "Reportes": vistas de mercado y reportes de consulta.
const NAV_REPORTES: NavItem[] = [
  { to: "/reportes/inventario", label: "Reporte de Inventario", perm: "contratos" },
  { to: "/reportes/edoctas", label: "Estados de cuenta CENACE", perm: "facturas_compra" },
  { to: "/b2b/utilidad", label: "Utilidad B2B", perm: "contratos" },
  { to: "/reportes/pnl-contable", label: "P&L Contable", perm: "pnl_contable" },
  { to: "/reportes/caja", label: "Reporte de Caja (90 días)", perm: "movimientos_bancarios" },
  // Mapa de Precios, Compras MDA, Ofertas de capacidad (MTR), Margen capacidad −
  // demanda, Dispersión de Horas Críticas y Periodo HC viven en el Portal
  // (portal-edocta.mppenergia.com) desde el 22-sep-2026.
  { to: "/sombra-cenace", label: "Sombra CENACE (Edo. de cuenta)", perm: "tbfin" },
  { to: "/tbfin/activacion", label: "Activación de strike (TBFin)", perm: "activacion_strike" },
  { to: "/reportes/cauchy", label: "Parámetros Cauchy (DART)", perm: "parametros_cauchy" },
  { to: "/reportes/memoria-rea", label: "Memoria de cálculo REA", perm: "memoria_rea" },
];

// Menú "Contratos": administración + facturación + ejercicio sombra.
const NAV_CONTRATOS: NavItem[] = [
  { to: "/contratos", label: "Administrar Contratos", perm: "contratos" },
  { to: "/b2b", label: "Back-to-Back", perm: "contratos" },
  { to: "/proyecciones", label: "Proyecciones B2B (carga)", perm: "contratos" },
  { to: "/facturacion", label: "Facturación", perm: "tbfin" },
  { to: "/facturas", label: "Facturas Emitidas", perm: "tbfin" },
  { to: "/facturas-compra", label: "Facturas de Compra CENACE", perm: "facturas_compra" },
  { to: "/facturas-recibidas", label: "Facturas Recibidas", perm: "facturas_recibidas" },
  { to: "/facturas-servicios", label: "Facturas de Servicios", perm: "facturas_servicios" },
  { to: "/sombra", label: "Ejercicio Sombra", perm: "tbfin", end: true },
  { to: "/sombra/guardados", label: "Ejercicios Sombra guardados", perm: "tbfin" },
  { to: "/escenarios", label: "Escenarios", perm: "escenarios" },
];

// Menú "Configuración".
const NAV_CONFIG: NavItem[] = [
  { to: "/tarifas-reguladas", label: "Tarifas Reguladas", perm: "treg" },
  { to: "/contrapartes", label: "Contrapartes", perm: "contrapartes" },
  { to: "/tipo-cambio", label: "Tipo de Cambio", perm: "movimientos_bancarios" },
  { to: "/credenciales", label: "Credenciales", perm: "credenciales" },
  { to: "/correo-escenarios", label: "Envío de correo de escenarios", perm: "admin" },
  { to: "/nodos-cauchy", label: "Nodos Cauchy", perm: "admin" },
  { to: "/arbitraje/centros-carga", label: "Centros de carga (arbitraje)", perm: "arbitraje" },
  { to: "/auditoria", label: "Auditoría", perm: "auditoria" },
  { to: "/admin", label: "Administración", perm: "admin" },
];

// Todas las hojas del menú (para resolver la etiqueta de un favorito).
const TODAS_LAS_HOJAS: NavItem[] = [
  ...hojas(NAV_OPERACION), ...NAV_REPORTES, ...NAV_CONTRATOS, ...NAV_CONFIG,
];

const tienePerm = (has: (p: string) => boolean, perm: string | string[]): boolean =>
  Array.isArray(perm) ? perm.some(has) : has(perm);

export function App() {
  const isAuthenticated = useIsAuthenticated();

  if (!DEV_BYPASS_AUTH && !isAuthenticated) {
    return <LoginPage />;
  }

  return (
    <AuthzProvider>
      <FavoritosProvider>
        <Shell />
      </FavoritosProvider>
    </AuthzProvider>
  );
}

function Pantalla({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-full flex items-center justify-center p-8">
      <div className="max-w-md text-center space-y-3">{children}</div>
    </div>
  );
}

/** Bolitas rojas del menú Contratos: meses con cálculo pendiente de capturar
 *  (falta facturar → /facturacion; falta ejercicio sombra → /sombra). */
function usePuntosPendientes(habilitado: boolean): Record<string, string> {
  const q = useQuery({
    queryKey: ["b2b-pendientes"],
    queryFn: getB2bPendientes,
    enabled: habilitado,
    staleTime: 5 * 60 * 1000,
    refetchInterval: 5 * 60 * 1000,
  });
  const fmt = (arr: { folio: string; meses: string[] }[]) =>
    arr.map((p) => `${p.folio}: ${p.meses.join(", ")}`).join(" · ");
  const puntos: Record<string, string> = {};
  if (q.data?.facturacion.length)
    puntos["/facturacion"] = `Falta facturar — ${fmt(q.data.facturacion)}`;
  if (q.data?.sombra.length)
    puntos["/sombra"] = `Falta ejercicio sombra — ${fmt(q.data.sombra)}`;
  return puntos;
}

function Shell() {
  const { instance } = useMsal();
  const { me, rol, has, isLoading, isError, error } = usePermisos();
  const puntosContratos = usePuntosPendientes(has("tbfin"));
  const { favoritos } = useFavoritos();

  const handleLogout = () => {
    if (DEV_BYPASS_AUTH) return;
    instance.logoutPopup({ postLogoutRedirectUri: window.location.origin });
  };

  if (isLoading) {
    return <Pantalla><p className="text-slate-500">Cargando perfil…</p></Pantalla>;
  }
  if (isError) {
    return (
      <Pantalla>
        <h1 className="text-lg font-semibold text-slate-900">No se pudo cargar tu perfil</h1>
        <p className="text-sm text-red-600">{error?.message}</p>
        <LogoutLink onLogout={handleLogout} />
      </Pantalla>
    );
  }
  if (!rol) {
    return (
      <Pantalla>
        <h1 className="text-lg font-semibold text-slate-900">Sin perfil asignado</h1>
        <p className="text-sm text-slate-600">
          Tu cuenta <span className="font-medium">{me?.email}</span> no tiene un perfil
          en el ETRM. Pide a un administrador que te asigne uno.
        </p>
        <LogoutLink onLogout={handleLogout} />
      </Pantalla>
    );
  }

  const operacionVisibles = NAV_OPERACION
    .map((n): NavEntrada | null => {
      if (!esGrupo(n)) return tienePerm(has, n.perm) ? n : null;
      const items = n.items.filter((i) => tienePerm(has, i.perm));
      return items.length ? { ...n, items } : null;
    })
    .filter((n): n is NavEntrada => n !== null);
  const contratosVisibles = NAV_CONTRATOS.filter((n) => tienePerm(has, n.perm));
  const reportesVisibles = NAV_REPORTES.filter((n) => tienePerm(has, n.perm));
  const configVisibles = NAV_CONFIG.filter((n) => tienePerm(has, n.perm));
  // Favoritos del usuario (localStorage), en el orden en que los marcó y solo
  // los que puede ver con su perfil.
  const favoritosVisibles: NavItem[] = favoritos
    .map((to) => TODAS_LAS_HOJAS.find((n) => n.to === to))
    .filter((n): n is NavItem => !!n && tienePerm(has, n.perm));
  // La landing NO es el primero del menú: es el Calendario (decisión del
  // usuario 9-oct-2026) y, sin ese permiso, el Cálculo TBFin.
  const home = ["/calendario", "/tbfin"].find(
    (to) => hojas(operacionVisibles).some((n) => n.to === to),
  ) ?? hojas(operacionVisibles)[0]?.to ?? contratosVisibles[0]?.to ?? reportesVisibles[0]?.to
    ?? configVisibles[0]?.to ?? "/";

  return (
    <div className="min-h-full flex flex-col">
      <header className="border-b bg-white px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-6">
          <Link to={home} className="flex items-center gap-3 hover:opacity-80">
            <img src="/logo-mark.png" alt="MPP Energía" className="w-7 h-7" />
            <div className="font-semibold text-lg">ETRM</div>
          </Link>
          <nav className="flex items-center gap-1 text-sm">
            {favoritosVisibles.length > 0 && (
              <NavMenu label="★ Favoritos" items={favoritosVisibles} />
            )}
            {contratosVisibles.length > 0 && (
              <NavMenu label="Contratos" items={contratosVisibles} puntos={puntosContratos} />
            )}
            {operacionVisibles.length > 0 && (
              <NavMenu label="Operación" items={operacionVisibles} />
            )}
            {reportesVisibles.length > 0 && <NavMenu label="Reportes" items={reportesVisibles} />}
            {configVisibles.length > 0 && <NavMenu label="Configuración" items={configVisibles} />}
          </nav>
        </div>
        <div className="flex items-center gap-4">
          <BusquedaGlobal />
          <ReaBadge />
          <HscBadge />
          <div className="text-right leading-tight">
            {/* El rol vive en el tooltip del correo: en el encabezado pesa más
                el semáforo de la e.firma que un dato que casi nunca cambia. */}
            <div className="text-sm text-slate-700 cursor-default"
              title={ROL_LABEL[rol] ?? rol ?? undefined}>
              {me?.email}
            </div>
            <FielBadge />
          </div>
          <button
            type="button"
            onClick={handleLogout}
            className="text-slate-500 hover:text-slate-900 inline-flex items-center gap-1 text-sm"
            title="Cerrar sesión"
          >
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </header>
      <main className="flex-1 px-6 py-6">
        <Routes>
          <Route path="/" element={<Navigate to={home} replace />} />
          <Route path="/tarifas-reguladas" element={<Guard perm="treg" home={home}><TarifasReguladasPage /></Guard>} />
          <Route path="/contratos" element={<Guard perm="contratos" home={home}><ContratosPage /></Guard>} />
          <Route path="/b2b" element={<Guard perm="contratos" home={home}><B2bPage /></Guard>} />
          <Route path="/proyecciones" element={<Guard perm="contratos" home={home}><ProyeccionesPage /></Guard>} />
          <Route path="/b2b/utilidad" element={<Guard perm="contratos" home={home}><B2bUtilidadPage /></Guard>} />
          <Route path="/contrapartes" element={<Guard perm="contrapartes" home={home}><ContrapartesPage /></Guard>} />
          <Route path="/tbfin" element={<Guard perm={["tbfin", "editar_folio"]} home={home}><TbfinPage /></Guard>} />
          <Route path="/facturacion" element={<Guard perm="tbfin" home={home}><FacturacionPage /></Guard>} />
          <Route path="/facturas" element={<Guard perm="tbfin" home={home}><FacturasPage /></Guard>} />
          <Route path="/facturas/:id" element={<Guard perm="tbfin" home={home}><FacturaDetallePage /></Guard>} />
          <Route path="/facturas-compra" element={<Guard perm="facturas_compra" home={home}><FacturasCompraPage /></Guard>} />
          <Route path="/facturas-recibidas" element={<Guard perm="facturas_recibidas" home={home}><FacturasRecibidasPage /></Guard>} />
          <Route path="/facturas-servicios" element={<Guard perm="facturas_servicios" home={home}><FacturasServiciosPage /></Guard>} />
          <Route path="/sombra" element={<Guard perm="tbfin" home={home}><SombraPage /></Guard>} />
          <Route path="/sombra/guardados" element={<Guard perm="tbfin" home={home}><SombrasGuardadasPage /></Guard>} />
          <Route path="/sombra/guardados/:id" element={<Guard perm="tbfin" home={home}><SombraGuardadaDetallePage /></Guard>} />
          <Route path="/sombra-cenace" element={<Guard perm="tbfin" home={home}><SombraCenacePage /></Guard>} />
          <Route path="/reportes/inventario" element={<Guard perm="contratos" home={home}><ReporteInventarioPage /></Guard>} />
          <Route path="/reportes/edoctas" element={<Guard perm="facturas_compra" home={home}><EdoctasList /></Guard>} />
          <Route path="/reportes/edoctas/:subcuenta/:fecha" element={<Guard perm="facturas_compra" home={home}><EdoctaDetail /></Guard>} />
          <Route path="/escenarios" element={<Guard perm="escenarios" home={home}><EscenariosPage /></Guard>} />
          <Route path="/calendario" element={<Guard perm="calendario" home={home}><MatrizPage /></Guard>} />
          <Route path="/oficios" element={<Guard perm="oficios" home={home}><OficiosPage /></Guard>} />
          <Route path="/posiciones-mercado" element={<Guard perm="posiciones_mercado" home={home}><PosicionesMercadoPage /></Guard>} />
          <Route path="/portafolio" element={<Guard perm="portafolio_cel_irec" home={home}><PortafolioPage /></Guard>} />
          <Route path="/intercambios" element={<Guard perm="intercambios" home={home}><IntercambiosPage /></Guard>} />
          <Route path="/pronosticos-luxem" element={<Guard perm="pronosticos_luxem" home={home}><PronosticosLuxemPage /></Guard>} />
          <Route path="/arbitraje" element={<Guard perm="arbitraje" home={home}><ArbitrajePage /></Guard>} />
          <Route path="/nominacion" element={<Guard perm="nominacion" home={home}><NominacionPage /></Guard>} />
          <Route path="/nominacion/dart" element={<Guard perm="nominacion" home={home}><NominacionDartPage /></Guard>} />
          <Route path="/movimientos-bancarios" element={<Guard perm="movimientos_bancarios" home={home}><MovimientosBancariosPage /></Guard>} />
          <Route path="/tipo-cambio" element={<Guard perm="movimientos_bancarios" home={home}><TipoCambioConfigPage /></Guard>} />
          <Route path="/proyeccion-caja" element={<Guard perm="movimientos_bancarios" home={home}><ProyeccionCajaPage /></Guard>} />
          <Route path="/arbitraje/centros-carga" element={<Guard perm="arbitraje" home={home}><ArbitrajeCentrosCargaPage /></Guard>} />
          <Route path="/registro-automatizado" element={<Guard perm="registro_automatizado" home={home}><RegistroAutomatizadoPage /></Guard>} />
          <Route path="/var-dart" element={<Guard perm="parametros_cauchy" home={home}><VarDartPage /></Guard>} />
          <Route path="/reportes/cauchy" element={<Guard perm="parametros_cauchy" home={home}><ParametrosCauchyPage /></Guard>} />
          <Route path="/reportes/pnl-contable" element={<Guard perm="pnl_contable" home={home}><PnlContablePage /></Guard>} />
          <Route path="/reportes/memoria-rea" element={<Guard perm="memoria_rea" home={home}><MemoriaReaPage /></Guard>} />
          <Route path="/reportes/caja" element={<Guard perm="movimientos_bancarios" home={home}><ReporteCajaPage /></Guard>} />
          <Route path="/garantias" element={<Guard perm="movimientos_bancarios" home={home}><GarantiasPage /></Guard>} />
          <Route path="/tbfin/activacion" element={<Guard perm="activacion_strike" home={home}><StrikeActivacionPage /></Guard>} />
          <Route path="/credenciales" element={<Guard perm="credenciales" home={home}><CredencialesPage /></Guard>} />
          <Route path="/correo-escenarios" element={<Guard perm="admin" home={home}><CorreoEscenariosPage /></Guard>} />
          <Route path="/nodos-cauchy" element={<Guard perm="admin" home={home}><NodosCauchyPage /></Guard>} />
          <Route path="/auditoria" element={<Guard perm="auditoria" home={home}><AuditoriaPage /></Guard>} />
          <Route path="/admin" element={<Guard perm="admin" home={home}><AdminPage /></Guard>} />
          <Route path="*" element={<Navigate to={home} replace />} />
        </Routes>
      </main>
    </div>
  );
}

// Semáforo REA en el encabezado, igual que en el Portal (misma fuente:
// public.monitoreo_rea, que llena el monitor del CENACE cada media hora).
// El color lo trae el propio dato; con más de 12 h sin refrescar se muestra un
// reloj en vez del bullet para no dar por vigente un semáforo viejo.
/**
 * Semáforo de la e.firma en el encabezado: la fecha de vencimiento a la vista
 * en toda la app, no solo en /credenciales. Clic → la página de credenciales.
 * Solo lo ve quien puede administrar sus credenciales (el endpoint exige ese
 * permiso) y desaparece si no hay certificado cargado.
 */
function FielBadge() {
  const { has } = usePermisos();
  const q = useQuery({
    queryKey: ["cenace-credenciales"],
    queryFn: getEstadoCredenciales,
    enabled: has("credenciales"),
    staleTime: 30 * 60_000,
    retry: false,
  });
  const fiel = q.data?.fiel;
  if (!fiel || fiel.error) return null;

  const v = estadoVigencia(fiel);
  return (
    <Link
      to="/credenciales"
      title={`e.firma — vence el ${fiel.vence ? fmtFecha(fiel.vence) : "?"}`}
      className={`inline-block my-0.5 text-[11px] leading-none rounded-full border px-2 py-0.5 hover:brightness-95 ${
        BADGE_VIGENCIA[v.tono]
      }`}
    >
      e.firma {v.texto}
    </Link>
  );
}

function ReaBadge() {
  const q = useQuery({
    queryKey: ["rea-ultimo"],
    queryFn: getReaUltimo,
    staleTime: 5 * 60_000,
    refetchInterval: 10 * 60_000,
  });
  const d = q.data;
  if (!d || d.pct == null) return null;

  const color = d.color || "#94a3b8";
  const actualizado = d.creado_en
    ? new Date(d.creado_en).toLocaleString("es-MX", {
        day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit",
      })
    : null;

  return (
    <div
      className="hidden sm:flex items-center gap-2 border rounded-lg px-3 py-1 leading-tight bg-white"
      title={
        (d.stale
          ? "REA vs MGP — SIN ACTUALIZAR"
          : `REA vs MGP${d.semaforo ? ` — ${d.semaforo}` : ""}`) +
        (actualizado ? ` (${actualizado})` : "")
      }
    >
      {d.stale ? (
        <Clock className="w-3.5 h-3.5 text-slate-400 shrink-0" aria-hidden="true" />
      ) : (
        <span
          className="inline-block w-2.5 h-2.5 rounded-full shrink-0"
          style={{ backgroundColor: color, boxShadow: `0 0 0 3px ${color}22` }}
          aria-hidden="true"
        />
      )}
      <div className="flex flex-col leading-tight">
        <span className="text-xs font-medium text-slate-700 tabular-nums">
          REA {d.pct.toFixed(1)}%
        </span>
        {actualizado && (
          <span className="text-[10px] text-slate-400 tabular-nums">{actualizado}</span>
        )}
      </div>
    </div>
  );
}

// Último HSC (Houston Ship Channel, USD/MMBtu) en el encabezado, con el cambio
// contra el día anterior. Se refresca solo cada 10 min; si no hay dato, no se pinta.
// Cuando el ETL está atrasado (marco rojo) y el usuario tiene el permiso
// 'capturar_hsc', el widget es clickeable y abre el popup de captura manual.
function HscBadge() {
  const { has } = usePermisos();
  const puedeCapturar = has("capturar_hsc");
  const [capturaAbierta, setCapturaAbierta] = useState(false);
  const q = useQuery({
    queryKey: ["hsc-ultimo"],
    queryFn: getHscUltimo,
    staleTime: 5 * 60_000,
    refetchInterval: 10 * 60_000,
  });
  const d = q.data;
  if (d?.valor == null) return null;

  const cambio = d.cambio;
  const sube = (cambio ?? 0) > 0;
  const baja = (cambio ?? 0) < 0;
  const nf = new Intl.NumberFormat("es-MX", {
    minimumFractionDigits: 3,
    maximumFractionDigits: 3,
  });
  const fecha = d.fecha
    ? new Date(d.fecha + "T00:00:00").toLocaleDateString("es-MX", {
        day: "2-digit", month: "2-digit", year: "2-digit",
      })
    : "";

  const atrasado = !d.al_dia;
  const clickeable = atrasado && puedeCapturar;
  return (
    <>
      <div
        onClick={clickeable ? () => setCapturaAbierta(true) : undefined}
        className={`hidden sm:block border rounded-lg px-3 py-1 leading-tight bg-white ${
          atrasado ? "border-red-400 ring-1 ring-red-300" : ""
        } ${clickeable ? "cursor-pointer hover:bg-red-50" : ""}`}
        title={
          atrasado
            ? `HSC ATRASADO: el último dato es del ${fecha} — después de las 8:00 ya debe existir el dato del día hábil en curso.` +
              (puedeCapturar
                ? " Da clic para capturar el valor a mano."
                : " Revisar el ETL de Bloomberg.")
            : "HSC — gas Houston Ship Channel (USD/MMBtu), último dato de Bloomberg (Platts Gas Daily)"
        }
      >
        <div className="flex items-center gap-1.5">
          <span className="text-[11px] font-semibold text-slate-400">HSC</span>
          <span className="text-sm font-bold text-slate-800 tabular-nums">{nf.format(d.valor)}</span>
          {cambio != null && (
            <span
              className={`text-xs font-medium tabular-nums ${
                sube ? "text-emerald-600" : baja ? "text-red-600" : "text-slate-400"
              }`}
            >
              {sube ? "↑" : baja ? "↓" : "="} {cambio >= 0 ? "+" : ""}
              {nf.format(cambio)}
            </span>
          )}
        </div>
        <div className={`text-[11px] ${atrasado ? "text-red-500 font-medium" : "text-slate-400"}`}>
          {fecha}
        </div>
      </div>
      {capturaAbierta && <HscCapturaModal onCerrar={() => setCapturaAbierta(false)} />}
    </>
  );
}

// Popup de captura manual del HSC (fecha + valor USD/MMBtu).
function HscCapturaModal({ onCerrar }: { onCerrar: () => void }) {
  const qc = useQueryClient();
  const [fecha, setFecha] = useState(() => new Date().toISOString().slice(0, 10));
  const [valor, setValor] = useState("");
  const [error, setError] = useState<string | null>(null);

  const guardar = useMutation({
    mutationFn: () => capturarHsc(fecha, Number(valor)),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["hsc-ultimo"] });
      onCerrar();
    },
    onError: (e) => setError(e instanceof Error ? e.message : "No se pudo guardar."),
  });

  const valido = !!fecha && Number(valor) > 0;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/30 flex items-center justify-center p-4"
      onClick={onCerrar}
    >
      <div
        className="bg-white rounded-lg shadow-xl border p-5 w-full max-w-sm space-y-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div>
          <h2 className="text-base font-semibold text-slate-900">Capturar HSC manual</h2>
          <p className="text-xs text-slate-500 mt-1">
            Gas Houston Ship Channel (USD/MMBtu). El dato queda marcado como captura manual;
            cuando el ETL de Bloomberg se ponga al día, lo sobreescribe con el oficial.
          </p>
        </div>
        <div className="flex gap-3">
          <label className="text-sm flex-1">
            <span className="block text-xs text-slate-500 mb-1">Fecha del dato</span>
            <input
              type="date"
              value={fecha}
              max={new Date().toISOString().slice(0, 10)}
              onChange={(e) => setFecha(e.target.value)}
              className="border rounded px-2 py-1.5 text-sm w-full"
            />
          </label>
          <label className="text-sm flex-1">
            <span className="block text-xs text-slate-500 mb-1">Valor (USD/MMBtu)</span>
            <input
              type="number"
              step="0.001"
              min="0"
              placeholder="p. ej. 2.345"
              value={valor}
              onChange={(e) => setValor(e.target.value)}
              className="border rounded px-2 py-1.5 text-sm w-full text-right"
              autoFocus
            />
          </label>
        </div>
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 rounded p-2.5 text-sm">
            {error}
          </div>
        )}
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onCerrar}
            className="border rounded px-3 py-2 text-sm text-slate-600 hover:bg-slate-50"
          >
            Cancelar
          </button>
          <button
            type="button"
            disabled={!valido || guardar.isPending}
            onClick={() => {
              setError(null);
              guardar.mutate();
            }}
            className="bg-blue-600 text-white rounded px-4 py-2 text-sm hover:bg-blue-700 disabled:opacity-50"
          >
            {guardar.isPending ? "Guardando…" : "Guardar"}
          </button>
        </div>
      </div>
    </div>
  );
}

/** Punto rojo de pendiente: el tooltip dice qué falta capturar. */
function PuntoRojo({ title }: { title: string }) {
  return (
    <span
      className="inline-block w-2 h-2 rounded-full bg-red-500 ml-1.5 align-middle"
      title={title}
    />
  );
}

function NavMenu({
  label,
  items,
  puntos,
}: {
  label: string;
  items: NavEntrada[];
  /** ruta → tooltip: pinta un punto rojo en esa opción (y en el propio menú) */
  puntos?: Record<string, string>;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const activo = hojas(items).some((i) =>
    i.end ? location.pathname === i.to : location.pathname.startsWith(i.to),
  );

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);
  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`px-2 py-1 rounded inline-flex items-center gap-1 ${
          activo ? "bg-slate-100 text-slate-900" : "text-slate-600 hover:bg-slate-50"
        }`}
      >
        {label}
        {puntos && Object.keys(puntos).length > 0 && (
          <PuntoRojo title={Object.values(puntos).join("\n")} />
        )}
        <ChevronDown className="w-3.5 h-3.5" />
      </button>
      {open && (
        <div className="absolute left-0 mt-1 min-w-max bg-white border rounded-lg shadow-lg py-1 z-20">
          {items.map((n) =>
            esGrupo(n) ? (
              <NavSubmenu key={n.grupo} grupo={n} />
            ) : (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                className={({ isActive }) =>
                  `flex items-center justify-between whitespace-nowrap px-3 py-1.5 text-sm ${
                    isActive ? "bg-slate-100 text-slate-900" : "text-slate-700 hover:bg-slate-50"
                  }`
                }
              >
                <span>
                  {n.label}
                  {puntos?.[n.to] && <PuntoRojo title={puntos[n.to]} />}
                </span>
                <EstrellaFav to={n.to} />
              </NavLink>
            ),
          )}
        </div>
      )}
    </div>
  );
}

function NavSubmenu({ grupo }: { grupo: NavGrupo }) {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const activo = grupo.items.some((i) =>
    i.end ? location.pathname === i.to : location.pathname.startsWith(i.to),
  );
  return (
    <div
      className="relative"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`w-full flex items-center justify-between gap-3 whitespace-nowrap px-3 py-1.5 text-sm ${
          activo || open ? "bg-slate-100 text-slate-900" : "text-slate-700 hover:bg-slate-50"
        }`}
      >
        {grupo.grupo}
        <ChevronRight className="w-3.5 h-3.5 text-slate-400" />
      </button>
      {open && (
        <div className="absolute left-full top-0 -mt-1 min-w-max bg-white border rounded-lg shadow-lg py-1 z-30">
          {grupo.items.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              className={({ isActive }) =>
                `flex items-center justify-between whitespace-nowrap px-3 py-1.5 text-sm ${
                  isActive ? "bg-slate-100 text-slate-900" : "text-slate-700 hover:bg-slate-50"
                }`
              }
            >
              <span>{n.label}</span>
              <EstrellaFav to={n.to} />
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
}

function Guard({
  perm,
  home,
  children,
}: {
  perm: string | string[];
  home: string;
  children: React.ReactNode;
}) {
  const { has } = usePermisos();
  return tienePerm(has, perm) ? <>{children}</> : <Navigate to={home} replace />;
}

function LogoutLink({ onLogout }: { onLogout: () => void }) {
  if (DEV_BYPASS_AUTH) return null;
  return (
    <button
      type="button"
      onClick={onLogout}
      className="text-sm text-slate-500 hover:text-slate-900 inline-flex items-center gap-1 mx-auto"
    >
      <LogOut className="w-4 h-4" /> Cerrar sesión
    </button>
  );
}
